/**
 * TIJD — Tijd & Klok rulebook.
 *
 * Sourced from DEFAULT_POLICY (Tijd entries) + MODULE_AUTHORING_GUIDE §4.
 *
 * v2 (mei 2026) — uitgebreid met hints voor DUUR / PLANNING / GEMIDDELDE /
 * SAMENGESTELD / AFSTAND / CONVERSIE / OMREKENEN / SCHEMA / TIJDZONES /
 * KALENDER, na cross-module drift-audit (15+ seed-numberlines die buiten
 * de oorspronkelijke hints vielen).
 */

import type { ModuleRulebook } from './types'

/**
 * Note on customTypeRoles below: the `clock` type is an example of a
 * fully visual/interactive custom question type. When we add the next
 * 100+ types, each one plugs in via a single entry like this — no
 * changes to defaultPolicy.ts or the pipeline.
 */

export const TIJD_RULEBOOK: ModuleRulebook = {
  moduleId: 'Tijd',
  shortCode: 'TIJD',
  displayName: 'Tijd',

  didactics: {
    principles: [
      'klok_lezen',
      'tijdsduur_berekenen',
      '12_24_uurs',
      'snelheid_formule',
    ],
    schoolbookReferences: ['Wereld in Getallen', 'Pluspunt'],
    misconceptionDomain: 'tijd',
    coreSummary:
      'Klok lezen, tijdsduur berekenen, kalenders, schemas en snelheidsberekeningen.',
  },

  notation: {
    forbiddenPatterns: [
      {
        pattern: '\\bkm/h\\b',
        label: 'Engelse snelheidsnotatie (gebruik km/u)',
      },
    ],
  },

  content: {
    distractorMisconceptionPattern:
      'eenheid|minuten|uren|seconden|klok|kalender|snelheid',
  },

  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: null,
    preferredStrategyKeywords: [
      'uren en minuten',
      'tijdsduur',
      '24-uurs notatie',
      'v = s / t',
    ],
  },

  visuals: {
    preferredAidTypes: [
      'clock',
      'digitalClock',
      'calendar',
      'timeline',
      'speedDiagram',
      'worldClockPanel',
      'rule',
    ],
    familyVisualHints: [
      // ── Klok-lezen ────────────────────────────────────────────────────
      {
        familyIdPattern: 'TIJD-ANALOOG',
        preferredStimulus: 'clock',
        note: 'Analoge klok als stimulus voor klok-lees-families.',
      },
      {
        familyIdPattern: 'TIJD-DIGITAAL',
        preferredStimulus: 'digitalClock',
        note: 'Digitale klok als stimulus.',
      },
      {
        familyIdPattern: 'TIJD-12-24',
        preferredStimulus: 'digitalClock',
        preferredSupport: 'clock',
        note: '12/24-uurs conversie: digitale weergave als primair, analoog als anker.',
      },

      // ── Kalender ──────────────────────────────────────────────────────
      {
        familyIdPattern: 'TIJD-KALENDER',
        preferredStimulus: 'calendar',
        note: 'Kalender-visual voor kalender-vragen.',
      },

      // ── Snelheid / Afstand ────────────────────────────────────────────
      {
        familyIdPattern: 'TIJD-SNELHEID',
        preferredStimulus: 'speedDiagram',
        note: 'Snelheid-diagram met v=s/t formule.',
      },
      {
        familyIdPattern: 'TIJD-AFSTAND',
        preferredStimulus: 'speedDiagram',
        preferredSupport: 'rule',
        note: 'Afstand uit snelheid+tijd: speedDiagram + formule-regel.',
      },

      // ── Tijdsduur / Planning — TIMELINE, niet numberline ──────────────
      {
        familyIdPattern: 'TIJD-DUUR',
        preferredStimulus: 'timeline',
        note: 'Tijdsduur visualiseert als tijdlijn met start/eind — niet als generieke getallenlijn.',
      },
      {
        familyIdPattern: 'TIJD-PLANNING',
        preferredStimulus: 'timeline',
        preferredSupport: 'rule',
        note: 'Planning op tijdlijn — gebeurtenissen na elkaar zichtbaar.',
      },
      {
        familyIdPattern: 'TIJD-SAMENGESTELD',
        preferredStimulus: 'timeline',
        note: 'Samengestelde tijdsberekeningen op één tijdlijn.',
      },
      {
        familyIdPattern: 'TIJD-GEMIDDELDE',
        preferredStimulus: 'speedDiagram',
        preferredSupport: 'rule',
        note: 'Gemiddelde snelheid: speedDiagram (v=s/t) toont de driehoek-relatie; formule als support. Voor pure gemiddelde-tijden mag timeline.',
      },
      {
        familyIdPattern: 'TIJD-SCHEMA',
        preferredStimulus: 'timeline',
        note: 'Schema/dienstregeling als tijdlijn.',
      },

      // ── Conversie / Omrekenen ─────────────────────────────────────────
      {
        familyIdPattern: 'TIJD-CONVERSIE|TIJD-OMREKENEN',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Tijdeenheden omrekenen (sec/min/uur/dag): regel-kaart met conversiefactoren als support.',
      },

      // ── Tijdzones / Wereldklok ────────────────────────────────────────
      {
        familyIdPattern: 'TIJD-TIJDZONES',
        preferredStimulus: 'worldClockPanel',
        note: 'Tijdzones met wereld-klokpanel.',
      },

      // ── Diagram (snelheid-tijd-grafiek e.d.) ──────────────────────────
      {
        familyIdPattern: 'TIJD-DIAGRAM',
        preferredSupport: 'speedDiagram',
        note: 'Snelheid-tijd-diagram als support bij interpretatievragen.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'fill_in',
      'clock',
      'schedule_reading',
      'estimation_slider',
    ],
    customTypeRoles: [
      {
        type: 'clock',
        didacticRole: 'visual',
        preferredDok: 2,
        appropriateTiers: { min: 1, max: 12 },
        reason:
          'Interactieve klok dwingt directe representatie — sterker dan tekstuele MC.',
      },
      {
        type: 'schedule_reading',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 5, max: 16 },
        reason:
          'Een schema interpreteren combineert tijdslezen + plannings-redenering.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^TIJD-(ANALOOG|DIGITAAL|12-24)',
        preferredTypes: ['clock', 'fill_in', 'multiple_choice'],
        discouragedTypes: ['estimation_slider', 'true_false_series'],
        note: 'Klok-aflezen: interactieve clock-renderer of exacte HH:MM-invoer — geen schatting of waar/onwaar.',
      },
      {
        familyIdPattern: '^TGL-TIJD-(DUUR|OMREKENEN|CONVERSIE|SAMENGESTELD)',
        preferredTypes: ['fill_in', 'multiple_choice', 'step_solver'],
        discouragedTypes: ['estimation_slider', 'true_false_series'],
        note: 'Tijdsduur en omrekenen: exact aantal uren + minuten — geen slider, geen oordeel.',
      },
      {
        familyIdPattern: '^TGL-TIJD-(SCHEMA|PLANNING|KALENDER)',
        preferredTypes: ['schedule_reading', 'multiple_choice', 'fill_in'],
        discouragedTypes: ['estimation_slider'],
        note: 'Schema/planning/kalender: aflezen + redeneren — schedule_reading-type of MC, geen slider.',
      },
      {
        familyIdPattern: '^TGL-TIJD-(SNELHEID|AFSTAND)',
        preferredTypes: ['fill_in', 'step_solver', 'multiple_choice'],
        discouragedTypes: ['true_false_series', 'estimation_slider'],
        note: 'Snelheid/afstand: v=s/t exact uitrekenen — geen oordeel of schatting.',
      },
      {
        familyIdPattern: '^TGL-TIJD-(GEMIDDELDE|DIAGRAM)',
        preferredTypes: ['fill_in', 'multiple_choice'],
        discouragedTypes: ['true_false_series'],
        note: 'Gemiddelde tijdsduur of diagram-aflezen: exact resultaat uit data.',
      },
      {
        familyIdPattern: '^TGL-TIJD-TIJDZONES',
        preferredTypes: ['multiple_choice', 'fill_in'],
        discouragedTypes: ['estimation_slider'],
        note: 'Tijdzones: uurverschil + lokale tijd — exact, geen schatting.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'hours-minutes-swap',
      label: 'Uren/minuten verwisselen',
      description:
        '2:15 als "vijftien over twee" lezen i.p.v. de juiste analoge interpretatie.',
    },
    {
      id: '12-24-confusion',
      label: '12/24-uurs verwarring',
      description: "15:00 als 5 uur lezen i.p.v. 3 uur 's middags.",
    },
    {
      id: 'speed-formula-swap',
      label: 'Snelheidsformule verwisselen',
      description:
        's = v × t verwarren met v = s / t bij snelheid-vraagstukken.',
    },
  ],

  keyRules: [
    'Klok-visuals verplicht voor analoge tijdvragen',
    'Tijdsduur altijd in uren EN minuten vermelden',
    'Trap-distractor: uren/minuten verwisselen',
    'Snelheid: km/u (niet km/h), formule v=s/t',
    '12-uurs vs. 24-uurs onderscheid expliciet maken',
    'DUUR/PLANNING/SCHEMA → timeline (niet numberline) — tijd heeft eenheden, getallenlijn niet',
  ],

  authoringGuideAnchor: '#4-tijd--tijd--klok',
}
